import type { Archive, ItemDefinition } from "../domain/types";
import { quantityOf } from "../domain/itemRegistry";
import { affinityTowardsPlayer } from "../domain/actors";

export interface CoreRuntimeContext {
  contextHistory: string;
  runtimeVars: Record<string, string>;
}

const slotLabels: Record<string, string> = {
  weapon: "武器",
  head: "头部",
  chest: "胴部",
  arms: "腕部",
  waist: "腰部",
  legs: "腿部",
  charm: "护石",
};

function numericSummary(item: ItemDefinition) {
  const meta = item.meta;
  if (meta.kind === "weapon")
    return `攻击${meta.attack}、会心${meta.affinity}、锐利度${meta.sharpnessTier}`;
  if (meta.kind === "armor")
    return `防御${meta.defense}、抗性 火${meta.resistances.fire}/水${meta.resistances.water}/雷${meta.resistances.thunder}/冰${meta.resistances.ice}/龙${meta.resistances.dragon}`;
  if (meta.kind === "charm") return `技能 ${meta.skills.join("、") || "无"}`;
  if (meta.kind === "material") return `稀有度${meta.rarity}`;
  if (meta.kind === "consumable") return `效果 ${meta.effectProfile}`;
  return `弹种 ${meta.ammoType}`;
}

function equipment(a: Archive) {
  return Object.entries(a.player.equipped)
    .map(([slot, equipped]) => {
      if (!equipped) return `${slotLabels[slot] ?? slot}=空`;
      const item = a.itemRegistry.definitions[equipped.itemKey];
      return item
        ? `${slotLabels[slot] ?? slot}=${item.name}(${item.key}；${numericSummary(item)})`
        : `${slotLabels[slot] ?? slot}=缺失物品(${equipped.itemKey})`;
    })
    .join("\n");
}

function inventory(a: Archive) {
  const buckets: Record<string, string[]> = {
    素材: [],
    武器: [],
    防具: [],
    护石: [],
    其他: [],
  };
  for (const item of Object.values(a.itemRegistry.definitions)) {
    const quantity = quantityOf(a.itemRegistry, item.key);
    if (quantity <= 0) continue;
    const bucket =
      item.meta.kind === "material"
        ? "素材"
        : item.meta.kind === "weapon"
          ? "武器"
          : item.meta.kind === "armor"
            ? "防具"
            : item.meta.kind === "charm"
              ? "护石"
              : "其他";
    buckets[bucket].push(
      `${item.name}(${item.key})×${quantity}；${numericSummary(item)}`,
    );
  }
  return (
    Object.entries(buckets)
      .filter(([, items]) => items.length)
      .map(([label, items]) => `${label}：${items.join("、")}`)
      .join("\n") || "空"
  );
}

function npcs(a: Archive, present: boolean) {
  const rows = Object.values(a.characters)
    .filter((character) =>
      present
        ? character.presence.status === "present"
        : character.presence.status !== "present",
    )
    .map((character) => {
      const relations =
        character.relations
          .map(
            (relation) =>
              `${relation.targetId}:${relation.label || "未命名关系"}/${relation.affinity.current}`,
          )
          .join("、") || "无";
      return `${character.id} ${character.identity.name}（${character.identity.race}/${character.identity.occupation}${character.identity.affiliation ? `/${character.identity.affiliation}` : ""}）｜位置=${character.presence.location || "未记录"}｜当前=${character.status.current}｜对玩家好感=${affinityTowardsPlayer(character)}｜关系=${relations}｜动机=${character.bio.currentMotive || "未记录"}｜近期=${character.bio.recentEvents.slice(-3).join("；") || "无"}`;
    });
  return rows.join("\n") || "无";
}

function monsters(a: Archive) {
  return (
    Object.values(a.monsters)
      .map((monster) => {
        const parts = Object.entries(monster.parts)
          .map(
            ([name, part]) =>
              `${name}:${part.durability}${part.broken ? "(破坏)" : ""}`,
          )
          .join("、");
        return `${monster.id} ${monster.name}${monster.individualName ? `「${monster.individualName}」` : ""}｜状态=${monster.status}｜体力${monster.health}/愤怒${monster.rage}/疲劳${monster.fatigue}｜部位=${parts}｜断尾=${monster.tailSevered ? "是" : "否"}｜位置=${monster.location || "未知"}｜伤痕=${monster.scars.join("、") || "无"}｜领域=${monster.territory.join("、") || "未知"}`;
      })
      .join("\n") || "无"
  );
}

function tasks(a: Archive) {
  const active = a.tasks.available.find(
    (task) => task.id === a.tasks.activeTaskId,
  );
  const available = a.tasks.available
    .filter((task) => task.status === "available")
    .map((task) => `${task.id} ${task.title}(${task.star}★)`)
    .join("、");
  const history = a.tasks.history
    .slice(-5)
    .map((task) => `${task.id} ${task.title}[${task.status}]`)
    .join("、");
  return `当前=${active ? `${active.id} ${active.title}：${active.description}` : "无"}\n可接=${available || "无"}\n近期历史=${history || "无"}`;
}

export function buildCoreRuntimeContext(a: Archive): CoreRuntimeContext {
  const player = a.player;
  const profile = player.profile;
  const identity = `${player.identity.name}｜代词=${player.identity.pronouns}｜种族=${profile.race.label}｜职业=${profile.profession.label}｜出生地=${profile.birthplace.label}`;
  const vitals = `体力=${player.vitals.health}，耐力=${player.vitals.stamina}；种族相对指数 血量${profile.race.indices.health}/耐力${profile.race.indices.stamina}/闪避${profile.race.indices.dodge}/攻击${profile.race.indices.attack}`;
  const hunter = profile.profession.hasHunterLicense
    ? `HR=${player.hunter.hr}，HR经验=${player.hunter.hrExp}，泽尼=${player.wallet.zenny}，公会点=${player.wallet.guildPoints}`
    : `无猎人资格；泽尼=${player.wallet.zenny}，公会点=${player.wallet.guildPoints}`;
  const derived = `攻击=${player.derived.attack}，防御=${player.derived.defense}，抗性 火${player.derived.resistances.fire}/水${player.derived.resistances.water}/雷${player.derived.resistances.thunder}/冰${player.derived.resistances.ice}/龙${player.derived.resistances.dragon}`;
  const proficiency = Object.entries(player.proficiency)
    .map(([weapon, value]) => `${weapon}=${value}`)
    .join("、");
  const scene = `${a.scene.location}｜第${a.gameTime.day}日 ${String(a.gameTime.hour).padStart(2, "0")}:00`;
  const worldEvents = a.worldEvents.slice(-8).join("\n") || "无";
  const pendingHunt = a.pendingHuntContext
    ? JSON.stringify(a.pendingHuntContext)
    : "无";
  const values = {
    player_identity: identity,
    player_vitals: vitals,
    hunter_status: hunter,
    equipment: equipment(a),
    derived_stats: derived,
    proficiency,
    on_scene_npcs: npcs(a, true),
    off_scene_npcs: npcs(a, false),
    monsters: monsters(a),
    tasks: tasks(a),
    inventory: inventory(a),
    scene,
    world_events: worldEvents,
    opening_context: a.openingContext,
    pending_crafting: a.pendingCraftingContext || "无",
    pending_hunt: pendingHunt,
    deep_summary: a.deepSummary.text || "无",
  };
  const runtimeVars: Record<string, string> = {
    ...values,
    玩家名: player.identity.name,
    玩家身份: identity,
    玩家体能: vitals,
    猎人状态: hunter,
    装备: values.equipment,
    派生数值: derived,
    武器熟练度: proficiency,
    在场NPC: values.on_scene_npcs,
    离场NPC: values.off_scene_npcs,
    怪物个体: values.monsters,
    任务: values.tasks,
    分类物品栏: values.inventory,
    场景: scene,
    世界事件: worldEvents,
    开局设定: a.openingContext,
    工房结果: values.pending_crafting,
    狩猎结算: pendingHunt,
    深度总结: values.deep_summary,
  };
  runtimeVars.runtime_context = [
    `【玩家身份】\n${identity}`,
    `【玩家体能】\n${vitals}`,
    `【猎人状态】\n${hunter}`,
    `【装备】\n${values.equipment}`,
    `【派生数值】\n${derived}`,
    `【武器熟练度】\n${proficiency}`,
    `【场景】\n${scene}`,
    `【在场 NPC】\n${values.on_scene_npcs}`,
    `【离场 NPC】\n${values.off_scene_npcs}`,
    `【怪物个体】\n${values.monsters}`,
    `【任务】\n${values.tasks}`,
    `【分类物品栏】\n${values.inventory}`,
    `【世界事件】\n${worldEvents}`,
    `【工房结果】\n${values.pending_crafting}`,
    `【狩猎结算】\n${pendingHunt}`,
  ].join("\n\n");
  runtimeVars["权威运行时状态"] = runtimeVars.runtime_context;
  return { contextHistory: runtimeVars.runtime_context, runtimeVars };
}
